import { SAVED_ACCOUNTS_KEY } from './storageKeys';

const PASSKEY_STORAGE_PREFIX = 'ac_passkey_';
const LEGACY_PASSKEY_STORAGE_PREFIX = 'ac_user_passkey_';

type SavedAccountPasskeyEntry = {
  email?: string;
  hasPasskey?: boolean;
  [key: string]: unknown;
};

export function normalizePasskeyEmail(email: string | null | undefined): string {
  return (email || '').trim().toLowerCase();
}

export function getPasskeyDeviceStorageKey(email: string | null | undefined): string | null {
  const normalized = normalizePasskeyEmail(email);
  return normalized ? `${PASSKEY_STORAGE_PREFIX}${normalized}` : null;
}

export function isPasskeyAvailableOnThisDevice(email: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;

  const key = getPasskeyDeviceStorageKey(email);
  if (!key) return false;

  try {
    const value = window.localStorage.getItem(key);
    return value === 'true' || value === '1';
  } catch {
    return false;
  }
}

export function syncSavedAccountPasskeyFlag(email: string | null | undefined, hasPasskey: boolean): boolean {
  if (typeof window === 'undefined') return false;

  const normalized = normalizePasskeyEmail(email);
  if (!normalized) return false;

  try {
    const stored = window.localStorage.getItem(SAVED_ACCOUNTS_KEY);
    if (!stored) return false;

    const accounts = JSON.parse(stored);
    if (!Array.isArray(accounts)) return false;

    const updated = (accounts as SavedAccountPasskeyEntry[]).map((account) => {
      if (normalizePasskeyEmail(account.email) !== normalized) return account;
      return { ...account, hasPasskey };
    });

    window.localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
}

export function syncAllSavedAccountPasskeyFlags(hasPasskey: boolean): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const stored = window.localStorage.getItem(SAVED_ACCOUNTS_KEY);
    if (!stored) return false;

    const accounts = JSON.parse(stored);
    if (!Array.isArray(accounts)) return false;

    const updated = (accounts as SavedAccountPasskeyEntry[]).map((account) => ({
      ...account,
      hasPasskey,
    }));

    window.localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
}

export function markPasskeyOnThisDevice(email: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;

  const key = getPasskeyDeviceStorageKey(email);
  if (!key) return false;

  try {
    window.localStorage.setItem(key, 'true');
    syncSavedAccountPasskeyFlag(email, true);
    return true;
  } catch {
    return false;
  }
}

export function clearPasskeyOnThisDevice(email: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;

  const key = getPasskeyDeviceStorageKey(email);
  if (!key) return false;

  try {
    window.localStorage.removeItem(key);
    syncSavedAccountPasskeyFlag(email, false);
    return true;
  } catch {
    return false;
  }
}

export function clearAllPasskeysOnThisDevice(): number {
  if (typeof window === 'undefined') return 0;

  let removed = 0;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(PASSKEY_STORAGE_PREFIX) || key?.startsWith(LEGACY_PASSKEY_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
      removed += 1;
    });
    syncAllSavedAccountPasskeyFlags(false);
  } catch {
    return removed;
  }
  return removed;
}
